/* ============================================================================
   MALSERA — ms-related.js
   Tira de productos relacionados, y la galería «Así se llevan».

   El desplazamiento es NATIVO: no hay carrusel de JavaScript. Este archivo
   solo hace dos cosas, y ninguna se ejecuta mientras se arrastra:
     · Traer el bloque por la API de recomendaciones si el servidor no pudo
       llenarlo.
     · Mover el contenedor con las flechas y encender o apagar su estado.

   Mientras la usuaria arrastra, aquí no corre nada: el gesto lo lleva el
   compositor del navegador.

   VA ENVUELTO EN UNA FUNCIÓN, y no es cosmético. Lo cargan dos secciones de
   la misma ficha (relacionados y la galería), cada una con su etiqueta
   <script>, y el navegador ejecuta las dos. Sin el envoltorio, la segunda
   ejecución vuelve a declarar la clase en el ámbito global y el script
   revienta. Por lo mismo, cada define comprueba antes si ya existe.
   ============================================================================ */

(() => {
  if (customElements.get('ms-relacionados')) return;

  class MsRelacionados extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;

      if (url) {
        // Primero la ficha, después esto, que vive muy por debajo del pliegue.
        const pedir = () => this.traer(url);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(pedir, { timeout: 2500 });
        } else {
          setTimeout(pedir, 900);
        }
        return;
      }

      this.conectarFlechas();
    }

    async traer(url) {
      try {
        const respuesta = await fetch(url);
        if (!respuesta.ok) return;

        const html = new DOMParser().parseFromString(await respuesta.text(), 'text/html');
        const nuevo = html.querySelector('[data-ms-relacionados]');

        // Sin productos suficientes, Liquid no devuelve tira y el hueco no
        // llega a existir. Mejor sin bloque que con un bloque vacío.
        if (!nuevo || !nuevo.querySelector('[data-ms-tira]')) return;

        this.innerHTML = nuevo.innerHTML;
        this.removeAttribute('data-url');
        this.conectarFlechas();
        document.dispatchEvent(new CustomEvent('ms:contenido-nuevo'));
      } catch (fallo) {
        /* El bloque es prescindible: si falla, la ficha sigue entera. */
      }
    }

    conectarFlechas() {
      const pista = this.querySelector('[data-ms-tira-track]');
      if (!pista) return;

      const prev = this.querySelector('[data-ms-tira-prev]');
      const next = this.querySelector('[data-ms-tira-next]');
      if (!prev || !next) return;

      // Se avanza un poco menos de una pantalla para que la foto del borde
      // siga visible y no se pierda el hilo de dónde estabas.
      const salto = () => Math.round(pista.clientWidth * 0.8);

      prev.addEventListener('click', () => pista.scrollBy({ left: -salto(), behavior: 'smooth' }));
      next.addEventListener('click', () => pista.scrollBy({ left: salto(), behavior: 'smooth' }));

      const revisar = () => {
        const maximo = pista.scrollWidth - pista.clientWidth - 1;
        prev.disabled = pista.scrollLeft <= 0;
        next.disabled = pista.scrollLeft >= maximo;
      };

      // Pasivo y con rAF: el manejador no puede retrasar el desplazamiento.
      let pendiente = false;
      pista.addEventListener(
        'scroll',
        () => {
          if (pendiente) return;
          pendiente = true;
          requestAnimationFrame(() => {
            revisar();
            pendiente = false;
          });
        },
        { passive: true }
      );

      if ('ResizeObserver' in window) {
        this.observador = new ResizeObserver(revisar);
        this.observador.observe(pista);
      }

      revisar();
    }

    disconnectedCallback() {
      this.observador?.disconnect();
    }
  }

  customElements.define('ms-relacionados', MsRelacionados);

  /**
   * Galería «Así se llevan». Mismo comportamiento que la tira de
   * relacionados: flechas sobre un contenedor de desplazamiento nativo. No
   * lleva data-url, así que nunca pide nada al servidor; solo conecta las
   * flechas. Un elemento personalizado no puede registrarse dos veces con el
   * mismo constructor, de ahí la subclase vacía.
   */
  customElements.define('ms-galeria-uso', class extends MsRelacionados {});
})();
